/**
 * Recertification (CTI) Overlay Component
 * 
 * Role-based access control:
 * - Physicians: Full write access (create/edit CTI)
 * - Staff/Admin: Read-only access (view only)
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ClipboardList, Save, Eye, Lock } from "lucide-react";
import { toast } from "sonner";

export function RecertificationOverlay({ patientId, patientName, onSave, onClose, userRole, existingData }) {
  // Check if user has write access (only physicians can write)
  const isPhysician = userRole?.toLowerCase() === "physician" || userRole?.toLowerCase() === "doctor" || userRole?.toLowerCase() === "md";
  const isReadOnly = !isPhysician;

  // Load existing data if provided (for view mode) - initialize in useState instead
  const getInitialFormData = () => {
    const defaultData = {
      benefit_period: "1",
      certification_date: new Date().toISOString().split("T")[0],
      f2f_date: "",
      f2f_physician: "",
      terminal_prognosis: true,
      prognosis_months: "6",
      primary_diagnosis: "",
      icd10_code: "",
      clinical_findings: "",
      functional_decline: [],
      symptom_burden: [],
      nutritional_status: "",
      physician_attestation: false,
      medical_director_review: false,
      notes: ""
    };
    if (existingData) {
      return { ...defaultData, ...existingData };
    }
    return defaultData;
  };

  const [formData, setFormData] = useState(getInitialFormData);

  const functionalDeclineOptions = [
    "Decline in ambulation",
    "Decline in ADLs",
    "Increased dependence for transfers",
    "Decline in cognitive function",
    "Increased fatigue/weakness",
    "Decline in oral intake",
    "Progressive weight loss",
    "Increased time in bed"
  ];

  const symptomOptions = [
    "Uncontrolled pain",
    "Dyspnea at rest or with minimal exertion",
    "Nausea/vomiting despite treatment",
    "Progressive weakness",
    "Recurrent infections",
    "Intractable symptoms",
    "Declining mental status"
  ];

  const toggleOption = (field, option) => {
    const current = formData[field];
    if (current.includes(option)) {
      setFormData({ ...formData, [field]: current.filter(o => o !== option) });
    } else {
      setFormData({ ...formData, [field]: [...current, option] });
    }
  };

  const handleSave = () => {
    if (!formData.f2f_date || !formData.clinical_findings) {
      toast.error("Please complete F2F date and clinical findings");
      return;
    }
    toast.success("Recertification documentation saved");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="recertification-overlay">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="w-5 h-5 text-purple-400" />
        <h3 className="font-semibold text-white">Recertification (CTI)</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
        {isReadOnly && (
          <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-500/50 ml-auto">
            <Eye className="w-3 h-3 mr-1" />
            View Only
          </Badge>
        )}
      </div>

      {isReadOnly && (
        <Alert className="bg-amber-900/20 border-amber-500/30">
          <Lock className="w-4 h-4 text-amber-400" />
          <AlertDescription className="text-amber-300">
            <strong>Read-only access:</strong> Only physicians can create or edit CTI documentation. 
            Staff and admin users have view-only access.
          </AlertDescription>
        </Alert>
      )}

      <div className="p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
        <p className="text-sm text-purple-300">
          Certification of Terminal Illness - Document clinical findings supporting 6-month prognosis
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Benefit Period</Label>
          <Select value={formData.benefit_period} onValueChange={v => setFormData({...formData, benefit_period: v})} disabled={isReadOnly}>
            <SelectTrigger className={`bg-slate-800 border-slate-700 text-white ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1st Benefit Period (90 days)</SelectItem>
              <SelectItem value="2">2nd Benefit Period (90 days)</SelectItem>
              <SelectItem value="3">3rd+ Benefit Period (60 days)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Certification Date</Label>
          <Input type="date" value={formData.certification_date} onChange={e => setFormData({...formData, certification_date: e.target.value})} className={`bg-slate-800 border-slate-700 text-white ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={isReadOnly} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Face-to-Face (F2F) Date</Label>
          <Input type="date" value={formData.f2f_date} onChange={e => setFormData({...formData, f2f_date: e.target.value})} className={`bg-slate-800 border-slate-700 text-white ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={isReadOnly} />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">F2F Encounter Physician</Label>
          <Input value={formData.f2f_physician} onChange={e => setFormData({...formData, f2f_physician: e.target.value})} placeholder="Dr. Name" className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={isReadOnly} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Primary Terminal Diagnosis</Label>
          <Input value={formData.primary_diagnosis} onChange={e => setFormData({...formData, primary_diagnosis: e.target.value})} placeholder="e.g., End-stage heart failure" className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={isReadOnly} />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">ICD-10 Code</Label>
          <Input value={formData.icd10_code} onChange={e => setFormData({...formData, icd10_code: e.target.value})} placeholder="e.g., I50.9" className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={isReadOnly} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Clinical Findings Supporting Terminal Prognosis</Label>
        <Textarea 
          value={formData.clinical_findings} 
          onChange={e => setFormData({...formData, clinical_findings: e.target.value})} 
          placeholder="Document specific clinical findings, lab values, and disease progression supporting the terminal prognosis..."
          rows={4}
          className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={isReadOnly}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Functional Decline Indicators</Label>
        <div className="flex flex-wrap gap-2">
          {functionalDeclineOptions.map(option => (
            <Badge 
              key={option}
              variant={formData.functional_decline.includes(option) ? "default" : "outline"}
              className={`${isReadOnly ? 'cursor-default' : 'cursor-pointer'} ${formData.functional_decline.includes(option) ? "bg-purple-600" : "border-slate-600 text-slate-300 hover:bg-slate-700"} ${isReadOnly && !formData.functional_decline.includes(option) ? 'opacity-40' : ''}`}
              onClick={() => !isReadOnly && toggleOption("functional_decline", option)}
            >
              {option}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Symptom Burden</Label>
        <div className="flex flex-wrap gap-2">
          {symptomOptions.map(option => (
            <Badge 
              key={option}
              variant={formData.symptom_burden.includes(option) ? "default" : "outline"}
              className={`${isReadOnly ? 'cursor-default' : 'cursor-pointer'} ${formData.symptom_burden.includes(option) ? "bg-red-600" : "border-slate-600 text-slate-300 hover:bg-slate-700"} ${isReadOnly && !formData.symptom_burden.includes(option) ? 'opacity-40' : ''}`}
              onClick={() => !isReadOnly && toggleOption("symptom_burden", option)}
            >
              {option}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Nutritional Status</Label>
        <Select value={formData.nutritional_status} onValueChange={v => setFormData({...formData, nutritional_status: v})} disabled={isReadOnly}>
          <SelectTrigger className={`bg-slate-800 border-slate-700 text-white ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}><SelectValue placeholder="Select status..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="adequate">Adequate oral intake</SelectItem>
            <SelectItem value="declining">Declining oral intake</SelectItem>
            <SelectItem value="minimal">Minimal oral intake</SelectItem>
            <SelectItem value="none">No longer taking food/fluids</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className={`space-y-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700 ${isReadOnly ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Physician Attestation Statement</Label>
          <Switch checked={formData.physician_attestation} onCheckedChange={c => setFormData({...formData, physician_attestation: c})} disabled={isReadOnly} />
        </div>
        <p className="text-xs text-slate-400">
          I certify that this patient has a terminal illness with a life expectancy of 6 months or less if the illness runs its normal course.
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isReadOnly && (
          <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
            <Save className="w-4 h-4 mr-1" />Save Recertification
          </Button>
        )}
      </div>
    </div>
  );
}

export default RecertificationOverlay;
